import {Component, OnDestroy, OnInit} from '@angular/core';
import {BsModalService} from "ngx-bootstrap/modal";
import {LoggerService} from "../../common/logger.service";
import {CartService} from "../cart.service";
import {SessionService} from "../../common/session.service";
import {AoCartService} from "../../ao-cart/ao-cart.service";
import {ShippingService} from "../../shipping/shipping.service";
import {ConfirmDialogService} from "../../common/confirm-dialog/confirm-dialog.service";
import {AlertUtilService} from "../../alert/alert-util.service";
import {TranslateService} from "../../translation/translate.service";
import {AnalyticsService} from "../../analytics/analytics.service";
import {AoCartOpenerService} from "../../ao-cart/ao-cart-opener.service";
import {BaseComponent} from "../../base.component";
import {from, noop, of, Subscription} from "rxjs";
import {ICart, IFreeShippingMessage} from "../cart-models";
import {Constants} from "../../constants";
import {PcUpsellModalComponent} from "../pc-upsell/pc-upsell-modal.component";
import {AutoOrder} from "../../autoorder/auto-order-models";
import * as _ from 'lodash';
import {CartSavedProductService} from "../cart-saved-product/cart-saved-product.service";
import {ISavedProduct} from "../cart-saved-product/cart-saved-product-models";
import {Router} from "@angular/router";
import {UserService} from "../../common/user.service";
import {map, switchMap, take} from "rxjs/operators";
import {UsanaCurrencyPipe} from "../../currency/usana-currency.pipe";
import {IPaymentMethod} from "../../../checkout/models/payment/payment-method";
import {GqlPaymentService} from "../../../checkout/payment/gql-payment.service";
import {ProductCreditUtil} from "../../util/product-credit-util";
import {MarkupService} from "../../common/markup.service";
import {CartUtil} from "../cart-util";

@Component({
    selector: 'cart-overview',
    templateUrl: './cart-overview.component.html',
    styleUrls: ['./cart-overview.component.scss'],
    standalone: false
})
export class CartOverviewComponent extends BaseComponent implements OnInit, OnDestroy {

    showRenewalFeeMessage: boolean = false;
    hideWillCall: boolean = false;
    hideFreeShippingMessage: boolean = false;
    productCreditBalanceText: string;
    hideProductCreditBalanceText: boolean = false;

    cart: ICart;
    cartBusy: boolean = false;
    checkoutButtonClicked: boolean = false;
    freeShippingMessage: IFreeShippingMessage;
    cartSubscriptions: Subscription;
    saveForLaterProducts: ISavedProduct[];

    constructor(loggerService: LoggerService,
                userService: UserService,
                private cartService: CartService,
                private sessionService: SessionService,
                private aoCartService: AoCartService,
                private shippingService: ShippingService,
                private confirmDialogService: ConfirmDialogService,
                private alertUtilService: AlertUtilService,
                private translateService: TranslateService,
                private router: Router,
                private usanaCurrencyPipe: UsanaCurrencyPipe,
                private analyticsService: AnalyticsService,
                private modalService: BsModalService,
                private aoCartOpenerService: AoCartOpenerService,
                private savedProductService: CartSavedProductService,
                private markupService: MarkupService,
                private gqlPaymentService: GqlPaymentService) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.alertUtilService.closeAllAlerts();
        this.cartSubscriptions = this.cartService.cart$.subscribe(cart => {
            if (!_.isEmpty(cart)) {
                this.cart = cart;
                this.getProductCreditsDisplay();
                this.updateFreeShippingMessage();
                this.setShowRenewalFeeMessage();
                this.analyticsService.viewCartEvent(this.cart);
            }
        });

        this.cartSubscriptions.add(this.savedProductService.getSavedForLaterProducts().subscribe());
        this.cartSubscriptions.add(this.savedProductService.savedProducts$.subscribe((products: ISavedProduct[]) => this.saveForLaterProducts = products));
        this.cartSubscriptions.add(this.cartService.cartBusy$.subscribe((cartBusy: boolean) => this.cartBusy = cartBusy));
        this.markupService.setTitleFromTranslationTag("cart.cart.overview.page.title");
    }

    ngOnDestroy(): void {
        this.cartSubscriptions.unsubscribe();
    }

    get checkoutAvailable(): boolean {
        return !!this.cart && !!this.cart.products && this.cart.products.length > 0 && !this.checkoutButtonClicked && !this.cartBusy;
    }

    get showWillCallOnlyMessage(): boolean {
        return this.cart?.willCallOnly && this.multiWarehouse?.enabled && this.multiWarehouse?.productBased && !this.hideWillCall && !this.eventShopping;
    }

    updateFreeShippingMessage() {
        this.shippingService.getFreeShippingMessage(this.cart.freeShippingEligible, this.cart.freeShippingThresholdRemaining)
            .pipe(take(1))
            .subscribe((freeShippingMessage) => this.freeShippingMessage = freeShippingMessage);
    }

    getProductCreditsDisplay() {
        this.gqlPaymentService.loadProductCredits().pipe(
            switchMap((productCredits: IPaymentMethod[]) => {
                if (productCredits?.length > 0) {
                    return this.usanaCurrencyPipe.transform(ProductCreditUtil.sum(productCredits)).pipe(take(1),
                        switchMap((formattedBalance: any) => {
                            return from(this.translateService.translateRequest({
                                tag: 'cart.product.credit.balance',
                                replacements: new Map([['amount', formattedBalance]])
                            })).pipe(map((translatedValue: string) => translatedValue));
                        })
                    );
                }
                return of(null);
            })
        ).subscribe((translatedValue: string) => {
            this.productCreditBalanceText = translatedValue;
        });
    }

    private goToCheckout() {
        this.sessionService.isPcUpgrade().then((isPcUpgrade: boolean) => {
            if (isPcUpgrade && this.allowPcSignup) {
                this.checkoutButtonClicked = false;
                this.router.navigate([this.ENROLL_PC_ROUTE]);
            } else {
                if (this.allowPcSignup && this.allowPcUpsellAds) {
                    this.checkoutButtonClicked = false;
                    this.cartService.pcUpsellModalRef = this.modalService.show(PcUpsellModalComponent, Object.assign({}, {class: 'pc-upsell'}));
                } else {
                    this.cartService.checkout()
                        .catch(noop)
                        .finally(() => this.checkoutButtonClicked = false);
                }
            }
        });
    }

    checkout(): void {
        if (this.checkoutAvailable) {
            this.checkoutButtonClicked = true;
            let hasUnsavedCarts = false;
            this.alertUtilService.closeAllAlerts();
            _.forEach(this.aoCartService.autoOrders, (autoOrder: AutoOrder) => {
                if (this.aoCartService.hasAoChanged(autoOrder)) {
                    hasUnsavedCarts = true;
                    this.confirmDialogService.open("md", {title: "cart.ao.check.title"}, [{
                        body: "cart.ao.check.body.text",
                        translate: true
                    }], {
                        name: "cart.ao.check.open.ao",
                        confirmAction: () => this.aoCartOpenerService.openAoModal(autoOrder.orderId)
                    }, {
                        name: "cart.ao.check.continue.checkout",
                        cancelAction: () => {
                            this.aoCartService.cancelAoChanges(autoOrder);
                            this.goToCheckout();
                        },
                        show: true
                    });
                    return;
                }
            });

            if (!hasUnsavedCarts) {
                this.goToCheckout();
            } else {
                this.checkoutButtonClicked = false;
            }
        }
    }

    setShowRenewalFeeMessage() {
        this.showRenewalFeeMessage = this.expiredCustomer && !!_.find(this.cart.products, (cartProduct) => cartProduct.product.usanaId === Constants.RENEWAL_FEE_820_PRODUCT_CODE);
    }

    getItemsTotalQuantityTag(qty: number): string {
        return CartUtil.getItemsTotalQuantityTag(qty);
    }
}
